import { useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { zodResolver } from "@hookform/resolvers/zod";
import { DefaultFormMoneyInput } from "../../../../../../common/components/default_form_money_input";
import { useEffect, useMemo } from "react";
import { DefaultFormDatePickerInput } from "../../../../../../common/components/default_form_date_picker_input";
import { FormNotaFiscalInput } from "./form_nota_fiscal_input";
import { ConfigurarPagamentoForm, ConfigurarPagamentoSchema } from "../../../schemas/ColetaPrecos_cadastro/configurar_pagamento";
import { ColetaPrecoFornecedor } from "../../../../domain/models/coleta_preco_fornecedor";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { FormMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { ConfigurarPagamentoColeta } from "../../../../domain/models/configurar_pagamento_coleta";
import { ColetaPreco } from "../../../../domain/models/coleta_preco";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import PrimaryButton from "../../../../../../common/components/primary_button";

/**
 * ConfigurarPagamentoModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  valorTotal: number | null;
  coletaPreco: ColetaPreco | null;
  fornecedor: ColetaPrecoFornecedor | null;
  configurarPagamentoColeta: ConfigurarPagamentoColeta | null;
  onClose: () => void;
  onSubmit: (form: ConfigurarPagamentoForm) => void;
  onDeleteConfigurarPagamentoSubmit: () => void;
};

export const ConfigurarPagamentoModal = ({
  open,
  saving,
  loading,
  valorTotal,
  coletaPreco,
  fornecedor,
  configurarPagamentoColeta,
  onClose,
  onSubmit,
  onDeleteConfigurarPagamentoSubmit,
}: Props) => {
  const hookForm = useForm<ConfigurarPagamentoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(ConfigurarPagamentoSchema),
  });

  const [pagamentoAvista, entrada, pagamentoEm] = hookForm.watch(['pagamentoAvista', 'entrada', 'pagamentoEm']);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Sim',
        style: {
          backgroundColor: '#45b6af',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 2,
        label: 'Não',
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  const pagamentoEmOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Projetar meses futuros',
      },
      {
        id: 2,
        label: 'Informar intervalo de dias',
      },
    ];
  }, [
  ]);

  useEffect(() => {
    if (open) {
      hookForm.reset({
        titulo: '',
        formaPagamento: '',
        notaFiscal: '',
        pagamentoAvista: 1,
        dataPagamento: undefined,
        pagamentoEm: undefined,
        entrada: 2,
        valorEntrada: undefined,
        dataEntrada: undefined,
        nroParcelas: undefined,
        dataPrimeiroPagamento: undefined,
        nroDiasEntreParcelas: undefined,
        informacoesGerais: '',
      });

      if (valorTotal !== null) hookForm.setValue('valorTotal', valorTotal);
      if (fornecedor !== null) hookForm.setValue('fornecedor', fornecedor.nomeFantasia ?? undefined);

      if (configurarPagamentoColeta) {
        hookForm.setValue('titulo', configurarPagamentoColeta.titulo ?? '');
        hookForm.setValue('formaPagamento', configurarPagamentoColeta.formaPagamento ?? '');
        hookForm.setValue('notaFiscal', configurarPagamentoColeta.notaFiscal ?? '');
        hookForm.setValue('pagamentoAvista', configurarPagamentoColeta.pagamentoAvista === true ? 1 : 2);
        hookForm.setValue('dataPagamento', configurarPagamentoColeta.dataPagamento ?? undefined);
        hookForm.setValue('pagamentoEm', configurarPagamentoColeta.pagamentoEm === true ? 1 : 2);
        hookForm.setValue('entrada', configurarPagamentoColeta.entrada === true ? 1 : 2);
        hookForm.setValue('valorEntrada', configurarPagamentoColeta.valorEntrada ?? undefined);
        hookForm.setValue('dataEntrada', configurarPagamentoColeta.dataEntrada ?? undefined);
        hookForm.setValue('nroParcelas', configurarPagamentoColeta.nroParcelas ?? undefined);
        hookForm.setValue('dataPrimeiroPagamento', configurarPagamentoColeta.dataPrimeiroPagamento ?? undefined);
        hookForm.setValue('nroDiasEntreParcelas', configurarPagamentoColeta.nroDiasEntreParcelas ?? undefined);
        hookForm.setValue('informacoesGerais', configurarPagamentoColeta.informacoesGerais ?? undefined);
      }
    }
  }, [
    open,
    valorTotal,
    fornecedor,
    configurarPagamentoColeta,
    hookForm,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title="Configurar Pagamento"
      readOnly={configurarPagamentoColeta !== null}
      leftButtonSlot={
        <div className="flex flex-row items-center gap-4">
          {
            configurarPagamentoColeta !== null &&
            <div className="w-[10rem]">
              <PrimaryButton
                buttonColor="red"
                onClick={onDeleteConfigurarPagamentoSubmit}>
                Excluir Configuração
              </PrimaryButton>
            </div>
          }
          {
            (coletaPreco && coletaPreco.idOrdemCompra) &&
            <span className="font-bold text-black dark:text-white">Ordem de compra {coletaPreco.nrOrdemCompra}</span>
          }
        </div>
      }
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <div className='w-full flex flex-col gap-2 py-2'>
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-x-2">
          <DefaultFormMoneyInput
            name='valorTotal'
            label='Valor de Total'
            readOnly={true}
            reactHookForm={hookForm}
          />

          <DefaultFormInput
            name='fornecedor'
            label='Fornecedor'
            readOnly={true}
            reactHookForm={hookForm}
          />
        </div>

        <DefaultFormInput
          name='titulo'
          label='Titulo'
          readOnly={configurarPagamentoColeta !== null}
          reactHookForm={hookForm}
        />

        <DefaultFormInput
          name='formaPagamento'
          label='Forma de Pagamento'
          readOnly={configurarPagamentoColeta !== null}
          reactHookForm={hookForm}
        />

        <FormNotaFiscalInput
          name='notaFiscal'
          label='Nota Fiscal Especial'
          readOnly={configurarPagamentoColeta !== null}
          reactHookForm={hookForm}
        />

        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-x-2">
          <FormRadioGroupInput
            name='pagamentoAvista'
            label='Pagamento à Vista'
            readOnly={configurarPagamentoColeta !== null}
            options={statusOptions}
            reactHookForm={hookForm}
            onValueChange={e => {
              if (e === 2) {
                hookForm.setValue('pagamentoEm', 1);
              } else {
                hookForm.setValue('pagamentoEm', undefined);
              }
            }}
          />
          {
            pagamentoAvista === 2 &&
            <FormRadioGroupInput
              name='entrada'
              label='Entrada'
              readOnly={configurarPagamentoColeta !== null}
              options={statusOptions}
              reactHookForm={hookForm}
            />
          }
          {
            (pagamentoAvista === 2 && entrada === 1) &&
            <>
              <DefaultFormMoneyInput
                name='valorEntrada'
                label='Valor de Entrada'
                readOnly={configurarPagamentoColeta !== null}
                reactHookForm={hookForm}
              />

              <DefaultFormDatePickerInput
                name='dataEntrada'
                label='Data da Entrada'
                readOnly={configurarPagamentoColeta !== null}
                reactHookForm={hookForm}
              />
            </>
          }
          {
            pagamentoAvista === 2 &&
            <FormRadioGroupInput
              name='pagamentoEm'
              label='Pagamento em'
              readOnly={configurarPagamentoColeta !== null}
              options={pagamentoEmOptions}
              reactHookForm={hookForm}
            />
          }
        </div>
        {
          pagamentoAvista === 1 &&
          <DefaultFormDatePickerInput
            name='dataPagamento'
            label='Data de Pagamento'
            readOnly={configurarPagamentoColeta !== null}
            reactHookForm={hookForm}
          />
        }
        {
          pagamentoAvista === 2 &&
          <div className="w-full flex flex-col gap-2">
            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-x-2 pt-2">
              <FormMaskedInput
                name='nroParcelas'
                label='Gerar Quantas Parcelas?'
                mask='0000'
                maskPlaceholder='____'
                readOnly={configurarPagamentoColeta !== null}
                reactHookForm={hookForm}
              />

              <DefaultFormDatePickerInput
                name='dataPrimeiroPagamento'
                label='Data do 1º Pagamento'
                readOnly={configurarPagamentoColeta !== null}
                reactHookForm={hookForm}
              />
            </div>

            {
              pagamentoEm === 2 &&
              <div className="w-full pt-2">
                <FormMaskedInput
                  name='nroDiasEntreParcelas'
                  label='Número de dias entre as parcelas?'
                  mask='000'
                  maskPlaceholder='___'
                  readOnly={configurarPagamentoColeta !== null}
                  reactHookForm={hookForm}
                />
              </div>
            }
          </div>
        }

        <div className="mt-1">
          <DefaultFormInput
            name='informacoesGerais'
            label='Informações Gerais'
            readOnly={configurarPagamentoColeta !== null}
            multiline={true}
            rows={4}
            reactHookForm={hookForm}
          />
        </div>
      </div>
    </BaseFormControlledModal>
  );
}